import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { debounce, isEqual } from "lodash";

import { JobsContext, SearchContext } from "context/providers";
import { formatAISkillSearchString } from "utils/helpers/search";

import CollapsiblePanel from "./CollapsiblePanel";
import CustomSearchBox from "./CustomSearchBox";

const TEXT_SEARCH_DEBOUNCE_MS = 350;

const TextSearchFilter = () => {
  const { searchState, setSearchState } = useContext(SearchContext);
  const { jobOpp } = useContext(JobsContext);

  const isJobPage = useMemo(() => !!jobOpp?.id, [jobOpp?.id]);
  const [inputValue, setInputValue] = useState("");

  const commitSearchRef = useRef();
  commitSearchRef.current = (value) => {
    setSearchState({
      ...searchState,
      customSearchQuery: value,
    });
  };

  const debouncedCommitSearch = useMemo(
    () =>
      debounce((value) => {
        commitSearchRef.current(value);
      }, TEXT_SEARCH_DEBOUNCE_MS),
    []
  );

  useEffect(
    () => () => debouncedCommitSearch.cancel(),
    [debouncedCommitSearch]
  );

  useEffect(() => {
    if (!isJobPage) {
      setInputValue(formatAISkillSearchString(searchState.skills?.required));
    }
  }, [isJobPage, searchState.skills]);

  useEffect(() => {
    setInputValue((prev) => {
      if (!isEqual(searchState.customSearchQuery, prev)) {
        return searchState.customSearchQuery || "";
      }
      return prev;
    });
  }, [searchState.customSearchQuery]);

  const handleChange = ({ target }) => {
    setInputValue(target.value);
    debouncedCommitSearch(target.value);
  };

  const handleClear = () => {
    debouncedCommitSearch.cancel();
    setInputValue("");
    setSearchState({
      ...searchState,
      customSearchQuery: "",
    });
  };

  if (isJobPage) {
    return null;
  }

  return (
    <div id="filter-group-text-search">
      <CollapsiblePanel
        header="Text Search"
        sectionKey="text-search"
        defaultCollapsed={true}
      >
        <CustomSearchBox
          id="text-search"
          onChange={handleChange}
          onClear={handleClear}
          value={inputValue}
          placeholder="Search..."
        />
      </CollapsiblePanel>
    </div>
  );
};

export default TextSearchFilter;
